import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CalendarDays,
  CalendarClock,
  Calculator,
  CheckSquare,
  Download,
  Hash,
  ListChecks,
  LoaderCircle,
  Percent,
  Plus,
  Save,
  Scale,
  Sparkles,
  Trash2,
  Type,
} from "lucide-react";
import {
  ConfirmDialog,
  FeedbackBanner,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";

const FIELD_TYPES = [
  { value: "text", label: "Texto", icon: Type },
  { value: "number", label: "Número", icon: Hash },
  { value: "date", label: "Fecha", icon: CalendarDays },
  { value: "boolean", label: "Sí / No", icon: CheckSquare },
  { value: "select", label: "Lista de opciones", icon: ListChecks },
  {
    value: "judicial_interest",
    label: "Intereses desde sentencias",
    icon: Calculator,
  },
  {
    value: "computed_percentage",
    label: "Porcentaje de un cálculo",
    icon: Percent,
  },
  {
    value: "computed_date",
    label: "Fecha calculada",
    icon: CalendarClock,
  },
  {
    value: "legal_contribution",
    label: "Aporte legal · Ley 5059",
    icon: Scale,
  },
  {
    value: "expediente",
    label: "Dato del expediente",
    icon: Sparkles,
  },
];

const EXPEDIENTE_PROPERTIES = [
  { value: "caratula", label: "Carátula" },
  { value: "fuero", label: "Fuero" },
  { value: "procedure_type", label: "Categoría IURIX" },
  { value: "materia", label: "Materia" },
  { value: "organismo", label: "Instancia actual" },
];

function newField() {
  return {
    id: `local_${crypto.randomUUID()}`,
    label: "",
    type: "text",
    required: false,
    options: [],
    valueSource: null,
    judicialInterest: null,
    computedPercentage: null,
    computedDate: null,
    legalContribution: null,
  };
}

function newOption(label = "") {
  return { id: `local_option_${crypto.randomUUID()}`, label };
}

function downloadFile({ blob, fileName }) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function payloadField(field) {
  return {
    ...(field.id.startsWith("field_") ? { id: field.id } : {}),
    clientId: field.id,
    label: field.label.trim(),
    type: field.type,
    required: field.required,
    options: field.type === "select"
      ? (field.options || []).map((option) => ({
          ...(option.id.startsWith("option_") ? { id: option.id } : {}),
          label: option.label.trim(),
        }))
      : [],
    valueSource: field.valueSource,
    judicialInterest: field.type === "judicial_interest"
      ? field.judicialInterest
      : null,
    computedPercentage: field.type === "computed_percentage"
      ? field.computedPercentage
      : null,
    computedDate: field.type === "computed_date"
      ? field.computedDate
      : null,
    legalContribution: field.type === "legal_contribution"
      ? field.legalContribution
      : null,
  };
}

export function WorkflowTemplateEditorPage({ templateId = null }) {
  const api = useApi();
  const { navigate } = useRouter();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState(() => [
    { ...newField(), label: "Nombre", required: true },
  ]);
  const [revision, setRevision] = useState(1);
  const [canManage, setCanManage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    const request = templateId
      ? api.getWorkflowTemplate(templateId)
      : api.listWorkflowTemplates();
    request
      .then((result) => {
        if (!active) return;
        if (templateId) {
          setName(result.name);
          setDescription(result.description);
          setFields(result.fields);
          setRevision(result.revision);
          setCanManage(Boolean(result.permissions?.canManage));
        } else {
          setCanManage(Boolean(result.permissions?.canManage));
        }
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [api, templateId]);

  const duplicateLabels = useMemo(() => {
    const counts = new Map();
    fields.forEach((field) => {
      const key = field.label.trim().toLocaleLowerCase("es-AR");
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });
    return new Set(
      [...counts.entries()]
        .filter(([, count]) => count > 1)
        .map(([label]) => label),
    );
  }, [fields]);

  const valid = name.trim().length >= 2
    && fields.every((field) => {
      if (!field.label.trim()) return false;
      if (field.type === "select") {
        const labels = (field.options || []).map(({ label }) => (
          label.trim().toLocaleLowerCase("es-AR")
        ));
        if (!labels.length || labels.some((label) => !label)) return false;
        if (new Set(labels).size !== labels.length) return false;
      }
      if (field.type === "judicial_interest") {
        const inputField = fields.find(
          ({ id }) => id === field.judicialInterest?.inputFieldId,
        );
        return Boolean(
          inputField
          && inputField.id !== field.id
          && inputField.type === "text"
          && !inputField.valueSource
          && !inputField.judicialInterest,
        );
      }
      if (field.type === "computed_percentage") {
        const inputField = fields.find(
          ({ id }) => id === field.computedPercentage?.inputFieldId,
        );
        const rate = Number(field.computedPercentage?.rate);
        return Boolean(
          inputField
          && inputField.id !== field.id
          && inputField.type === "judicial_interest"
          && Number.isFinite(rate)
          && rate > 0
          && rate <= 100,
        );
      }
      if (field.type === "computed_date") {
        const inputField = fields.find(
          ({ id }) => id === field.computedDate?.inputFieldId,
        );
        const sourceProperty = field.computedDate?.sourceProperty;
        const rawOffsetDays = field.computedDate?.offsetDays;
        const offsetDays = rawOffsetDays === "" || rawOffsetDays == null
          ? Number.NaN
          : Number(rawOffsetDays);
        const validSource = inputField?.type === "date"
          ? sourceProperty === "value"
          : inputField?.type === "judicial_interest"
            && ["cutoffDate", "calculationDate"].includes(sourceProperty);
        return Boolean(
          inputField
          && inputField.id !== field.id
          && validSource
          && Number.isInteger(offsetDays)
          && offsetDays >= 0
          && offsetDays <= 3650,
        );
      }
      if (field.type === "legal_contribution") {
        const inputField = fields.find(
          ({ id }) => id === field.legalContribution?.inputFieldId,
        );
        const cuijField = fields.find(
          ({ id }) => id === field.legalContribution?.cuijFieldId,
        );
        return Boolean(
          inputField
          && inputField.id !== field.id
          && inputField.type === "judicial_interest"
          && cuijField
          && cuijField.id !== field.id
          && cuijField.type === "text"
          && !cuijField.valueSource
          && field.legalContribution?.ruleset === "mendoza_ley_5059",
        );
      }
      if (!field.valueSource) return true;
      const inputField = fields.find(
        ({ id }) => id === field.valueSource.inputFieldId,
      );
      return field.type === "text"
        && field.valueSource.kind === "expediente"
        && EXPEDIENTE_PROPERTIES.some(({ value }) => (
          value === field.valueSource.property
        ))
        && inputField
        && inputField.id !== field.id
        && inputField.type === "text"
        && !inputField.valueSource;
    })
    && !duplicateLabels.size;

  const updateField = (id, patch) => {
    setSaved(false);
    setFields((current) => current.map((field) => (
      field.id === id ? { ...field, ...patch } : field
    )));
  };

  const moveField = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= fields.length) return;
    setSaved(false);
    setFields((current) => {
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  };

  const updateOption = (fieldId, optionId, label) => {
    setSaved(false);
    setFields((current) => current.map((field) => (
      field.id === fieldId
        ? {
            ...field,
            options: field.options.map((option) => (
              option.id === optionId ? { ...option, label } : option
            )),
          }
        : field
    )));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!valid || saving || !canManage) return;
    setSaving(true);
    setSaved(false);
    setError("");
    const input = {
      name: name.trim(),
      description: description.trim(),
      fields: fields.map(payloadField),
    };
    try {
      const template = templateId
        ? await api.updateWorkflowTemplate(templateId, input)
        : await api.createWorkflowTemplate(input);
      setFields(template.fields);
      setRevision(template.revision);
      setSaved(true);
      if (!templateId) {
        navigate(`/flujos/${template.id}/configuracion`, { replace: true });
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const exportWorkflow = async () => {
    if (!templateId || exporting) return;
    setExporting(true);
    setError("");
    try {
      downloadFile(await api.exportWorkflowTemplate(templateId));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setExporting(false);
    }
  };

  const archiveWorkflow = async () => {
    if (!templateId || archiving || !canManage) return;
    setArchiving(true);
    setError("");
    try {
      await api.archiveWorkflowTemplate(templateId);
      navigate("/flujos", { replace: true });
    } catch (requestError) {
      setError(requestError.message);
      setArchiveConfirmationOpen(false);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="page workflow-template-editor-page">
      <PageHeader
        eyebrow="Praxis · Flujos"
        title={templateId ? "Configurar flujo" : "Nuevo flujo"}
        description="Definí la información básica que tendrá cada registro de este flujo."
        actions={(
          <div className="workflow-template-page-actions">
            <button
              className="button secondary"
              type="button"
              onClick={() => navigate(
                templateId ? `/flujos/${templateId}` : "/flujos",
              )}
            >
              <ArrowLeft size={16} />
              Volver
            </button>
            {templateId && (
              <button
                className="button secondary"
                type="button"
                disabled={loading || exporting}
                onClick={exportWorkflow}
              >
                {exporting
                  ? <LoaderCircle className="spin" size={16} />
                  : <Download size={16} />}
                {exporting ? "Exportando…" : "Exportar configuración"}
              </button>
            )}
          </div>
        )}
      />

      {error && <FeedbackBanner tone="error">{error}</FeedbackBanner>}
      {saved && (
        <FeedbackBanner tone="success">
          Configuración guardada correctamente.
        </FeedbackBanner>
      )}

      {loading ? (
        <div className="workflow-templates-loading">
          <LoaderCircle className="spin" size={23} />
          <span>Cargando configuración…</span>
        </div>
      ) : (
        <form className="workflow-template-form" onSubmit={save}>
          <section className="workflow-template-settings-card">
            <div className="workflow-template-section-heading">
              <div>
                <span className="eyebrow">Datos generales</span>
                <h2>Identificá el flujo</h2>
              </div>
              {templateId && <small>Revisión {revision}</small>}
            </div>
            <div className="workflow-template-general-fields">
              <label>
                Nombre del flujo
                <input
                  type="text"
                  value={name}
                  maxLength={120}
                  disabled={!canManage}
                  placeholder="Ej. Seguimiento de cobros"
                  onChange={(event) => {
                    setName(event.target.value);
                    setSaved(false);
                  }}
                />
              </label>
              <label>
                Descripción <span>Opcional</span>
                <textarea
                  value={description}
                  maxLength={500}
                  rows={3}
                  disabled={!canManage}
                  placeholder="Explicá brevemente para qué se utiliza este flujo."
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setSaved(false);
                  }}
                />
              </label>
            </div>
          </section>

          <section className="workflow-template-settings-card">
            <div className="workflow-template-section-heading">
              <div>
                <span className="eyebrow">Estructura</span>
                <h2>Columnas</h2>
                <p>
                  Usá campos básicos o completalos automáticamente desde un expediente.
                </p>
              </div>
              <strong>{fields.length}/50</strong>
            </div>

            <div className="workflow-field-list">
              {fields.map((field, index) => {
                const normalizedLabel = field.label
                  .trim()
                  .toLocaleLowerCase("es-AR");
                const duplicated = normalizedLabel
                  && duplicateLabels.has(normalizedLabel);
                const selectedFieldType = field.valueSource?.kind
                  || field.type;
                const FieldIcon = FIELD_TYPES.find(
                  ({ value }) => value === selectedFieldType,
                )?.icon || Type;
                const sourceCandidates = fields.filter((candidate) => (
                  candidate.id !== field.id
                  && candidate.type === "text"
                  && !candidate.valueSource
                ));
                const interestCandidates = fields.filter((candidate) => (
                  candidate.id !== field.id
                  && candidate.type === "judicial_interest"
                ));
                const dateCandidates = fields.filter((candidate) => (
                  candidate.id !== field.id
                  && ["date", "judicial_interest"].includes(candidate.type)
                ));
                const computedDateSource = fields.find(
                  ({ id }) => id === field.computedDate?.inputFieldId,
                );
                return (
                  <article className="workflow-field-row" key={field.id}>
                    <span className="workflow-field-order">{index + 1}</span>
                    <span className="workflow-field-type-icon">
                      <FieldIcon size={17} />
                    </span>
                    <label className={duplicated ? "has-error" : ""}>
                      Nombre de columna
                      <input
                        type="text"
                        value={field.label}
                        maxLength={80}
                        disabled={!canManage}
                        placeholder="Ej. Fecha de vencimiento"
                        onChange={(event) => updateField(field.id, {
                          label: event.target.value,
                        })}
                      />
                      {duplicated && <small>El nombre está repetido.</small>}
                    </label>
                    <label>
                      Tipo
                      <select
                        value={selectedFieldType}
                        disabled={!canManage}
                        onChange={(event) => {
                          const nextType = event.target.value;
                          if (nextType === "expediente") {
                            updateField(field.id, {
                              type: "text",
                              judicialInterest: null,
                              computedPercentage: null,
                              computedDate: null,
                              legalContribution: null,
                              valueSource: {
                                kind: "expediente",
                                inputFieldId: sourceCandidates[0]?.id || "",
                                property: "caratula",
                              },
                            });
                            return;
                          }
                          if (nextType === "judicial_interest") {
                            updateField(field.id, {
                              type: "judicial_interest",
                              options: [],
                              valueSource: null,
                              judicialInterest: {
                                inputFieldId: sourceCandidates[0]?.id || "",
                              },
                              computedPercentage: null,
                              computedDate: null,
                              legalContribution: null,
                            });
                            return;
                          }
                          if (nextType === "computed_percentage") {
                            updateField(field.id, {
                              type: "computed_percentage",
                              options: [],
                              valueSource: null,
                              judicialInterest: null,
                              computedPercentage: {
                                inputFieldId: interestCandidates[0]?.id || "",
                                rate: 2,
                              },
                              computedDate: null,
                              legalContribution: null,
                            });
                            return;
                          }
                          if (nextType === "computed_date") {
                            const inputField = dateCandidates[0];
                            updateField(field.id, {
                              type: "computed_date",
                              options: [],
                              valueSource: null,
                              judicialInterest: null,
                              computedPercentage: null,
                              computedDate: {
                                inputFieldId: inputField?.id || "",
                                sourceProperty: inputField?.type === "date"
                                  ? "value"
                                  : "cutoffDate",
                                offsetDays: "",
                              },
                              legalContribution: null,
                            });
                            return;
                          }
                          if (nextType === "legal_contribution") {
                            updateField(field.id, {
                              type: "legal_contribution",
                              options: [],
                              valueSource: null,
                              judicialInterest: null,
                              computedPercentage: null,
                              computedDate: null,
                              legalContribution: {
                                inputFieldId: interestCandidates[0]?.id || "",
                                cuijFieldId: sourceCandidates[0]?.id || "",
                                ruleset: "mendoza_ley_5059",
                              },
                            });
                            return;
                          }
                          updateField(field.id, {
                            type: nextType,
                            options: nextType === "select"
                              ? (field.options?.length
                                  ? field.options
                                  : [newOption(), newOption()])
                              : [],
                            valueSource: null,
                            judicialInterest: null,
                            computedPercentage: null,
                            computedDate: null,
                            legalContribution: null,
                          });
                        }}
                      >
                        {FIELD_TYPES.map(({ value, label }) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="workflow-field-required">
                      <input
                        type="checkbox"
                        checked={field.required}
                        disabled={!canManage}
                        onChange={(event) => updateField(field.id, {
                          required: event.target.checked,
                        })}
                      />
                      Obligatoria
                    </label>
                    {canManage && (
                      <div className="workflow-field-actions">
                        <button
                          type="button"
                          disabled={index === 0}
                          aria-label={`Subir ${field.label || "columna"}`}
                          onClick={() => moveField(index, -1)}
                        ><ArrowUp size={15} /></button>
                        <button
                          type="button"
                          disabled={index === fields.length - 1}
                          aria-label={`Bajar ${field.label || "columna"}`}
                          onClick={() => moveField(index, 1)}
                        ><ArrowDown size={15} /></button>
                        <button
                          className="is-danger"
                          type="button"
                          aria-label={`Eliminar ${field.label || "columna"}`}
                          onClick={() => {
                            setSaved(false);
                            setFields((current) => current.filter(
                              ({ id }) => id !== field.id,
                            ));
                          }}
                        ><Trash2 size={15} /></button>
                      </div>
                    )}
                    {field.valueSource?.kind === "expediente" && (
                      <div className="workflow-field-smart-config">
                        <span className="workflow-field-smart-heading">
                          <Sparkles size={15} />
                          Completar automáticamente
                        </span>
                        <label>
                          Dato a obtener
                          <select
                            value={field.valueSource.property}
                            disabled={!canManage}
                            onChange={(event) => updateField(field.id, {
                              valueSource: {
                                ...field.valueSource,
                                property: event.target.value,
                              },
                            })}
                          >
                            {EXPEDIENTE_PROPERTIES.map(({ value, label }) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Buscar usando
                          <select
                            value={field.valueSource.inputFieldId}
                            disabled={!canManage || !sourceCandidates.length}
                            onChange={(event) => updateField(field.id, {
                              valueSource: {
                                ...field.valueSource,
                                inputFieldId: event.target.value,
                              },
                            })}
                          >
                            {!sourceCandidates.length && (
                              <option value="">Agregá una columna de texto</option>
                            )}
                            {sourceCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label || "Columna sin nombre"}
                              </option>
                            ))}
                          </select>
                        </label>
                        {!sourceCandidates.length && (
                          <small>
                            Necesitás otra columna de texto que contenga el CUIJ.
                          </small>
                        )}
                      </div>
                    )}
                    {field.type === "judicial_interest" && (
                      <div className="workflow-field-smart-config">
                        <span className="workflow-field-smart-heading">
                          <Calculator size={15} />
                          Preparar desde sentencias del expediente
                        </span>
                        <label>
                          Buscar usando
                          <select
                            value={field.judicialInterest?.inputFieldId || ""}
                            disabled={!canManage || !sourceCandidates.length}
                            onChange={(event) => updateField(field.id, {
                              judicialInterest: {
                                inputFieldId: event.target.value,
                              },
                            })}
                          >
                            {!sourceCandidates.length && (
                              <option value="">Agregá una columna de texto con el CUIJ</option>
                            )}
                            {sourceCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label || "Columna sin nombre"}
                              </option>
                            ))}
                          </select>
                        </label>
                        <small className="workflow-field-smart-note">
                          El usuario elegirá hasta tres sentencias disponibles en Documentos.
                        </small>
                      </div>
                    )}
                    {field.type === "computed_percentage" && (
                      <div className="workflow-field-smart-config">
                        <span className="workflow-field-smart-heading">
                          <Percent size={15} />
                          Calcular automáticamente
                        </span>
                        <label>
                          Tomar el total de
                          <select
                            value={field.computedPercentage?.inputFieldId || ""}
                            disabled={!canManage || !interestCandidates.length}
                            onChange={(event) => updateField(field.id, {
                              computedPercentage: {
                                ...field.computedPercentage,
                                inputFieldId: event.target.value,
                              },
                            })}
                          >
                            {!interestCandidates.length && (
                              <option value="">Agregá una columna de intereses</option>
                            )}
                            {interestCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label || "Cálculo sin nombre"}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Porcentaje
                          <div className="workflow-percentage-input">
                            <input
                              type="number"
                              min="0.01"
                              max="100"
                              step="0.01"
                              value={field.computedPercentage?.rate ?? 2}
                              disabled={!canManage}
                              onChange={(event) => updateField(field.id, {
                                computedPercentage: {
                                  ...field.computedPercentage,
                                  rate: event.target.value,
                                },
                              })}
                            />
                            <span>%</span>
                          </div>
                        </label>
                        <small className="workflow-field-smart-note">
                          Se calculará por separado para lo que prospera, lo que rechaza y el total.
                        </small>
                      </div>
                    )}
                    {field.type === "computed_date" && (
                      <div className="workflow-field-smart-config">
                        <span className="workflow-field-smart-heading">
                          <CalendarClock size={15} />
                          Calcular automáticamente
                        </span>
                        <label>
                          Fecha de referencia
                          <select
                            value={field.computedDate?.inputFieldId || ""}
                            disabled={!canManage || !dateCandidates.length}
                            onChange={(event) => {
                              const source = fields.find(
                                ({ id }) => id === event.target.value,
                              );
                              updateField(field.id, {
                                computedDate: {
                                  ...field.computedDate,
                                  inputFieldId: event.target.value,
                                  sourceProperty: source?.type === "date"
                                    ? "value"
                                    : "cutoffDate",
                                },
                              });
                            }}
                          >
                            {!dateCandidates.length && (
                              <option value="">Agregá una fecha o cálculo de intereses</option>
                            )}
                            {dateCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label || "Fecha sin nombre"}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Fecha a usar
                          <select
                            value={field.computedDate?.sourceProperty || ""}
                            disabled={!canManage || !computedDateSource}
                            onChange={(event) => updateField(field.id, {
                              computedDate: {
                                ...field.computedDate,
                                sourceProperty: event.target.value,
                              },
                            })}
                          >
                            {computedDateSource?.type === "date" ? (
                              <option value="value">Fecha ingresada</option>
                            ) : (
                              <>
                                <option value="cutoffDate">Fecha de corte</option>
                                <option value="calculationDate">Fecha del cálculo</option>
                              </>
                            )}
                          </select>
                        </label>
                        <label>
                          Días después
                          <input
                            type="number"
                            min="0"
                            max="3650"
                            step="1"
                            value={field.computedDate?.offsetDays ?? ""}
                            disabled={!canManage}
                            onChange={(event) => updateField(field.id, {
                              computedDate: {
                                ...field.computedDate,
                                offsetDays: event.target.value,
                              },
                            })}
                          />
                        </label>
                        <small className="workflow-field-smart-note">
                          Se suman días corridos a la fecha seleccionada.
                        </small>
                      </div>
                    )}
                    {field.type === "legal_contribution" && (
                      <div className="workflow-field-smart-config">
                        <span className="workflow-field-smart-heading">
                          <Scale size={15} />
                          Aplicar reglas legales versionadas
                        </span>
                        <label>
                          Cálculo base
                          <select
                            value={field.legalContribution?.inputFieldId || ""}
                            disabled={!canManage || !interestCandidates.length}
                            onChange={(event) => updateField(field.id, {
                              legalContribution: {
                                ...field.legalContribution,
                                inputFieldId: event.target.value,
                              },
                            })}
                          >
                            {!interestCandidates.length && (
                              <option value="">Agregá una columna de intereses</option>
                            )}
                            {interestCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label || "Cálculo sin nombre"}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          CUIJ del expediente
                          <select
                            value={field.legalContribution?.cuijFieldId || ""}
                            disabled={!canManage || !sourceCandidates.length}
                            onChange={(event) => updateField(field.id, {
                              legalContribution: {
                                ...field.legalContribution,
                                cuijFieldId: event.target.value,
                              },
                            })}
                          >
                            {!sourceCandidates.length && (
                              <option value="">Agregá una columna de texto con el CUIJ</option>
                            )}
                            {sourceCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label || "Columna sin nombre"}
                              </option>
                            ))}
                          </select>
                        </label>
                        <small className="workflow-field-smart-note">
                          Ley 5059 · reglas vigentes desde 01/04/2026. La base,
                          las costas y los obligados quedan disponibles para revisión.
                        </small>
                      </div>
                    )}
                    {field.type === "select" && !field.valueSource && (
                      <div className="workflow-field-options-config">
                        <span className="workflow-field-smart-heading">
                          <ListChecks size={15} />
                          Opciones de la lista
                        </span>
                        <div className="workflow-field-option-list">
                          {(field.options || []).map((option, optionIndex) => (
                            <div
                              className="workflow-field-option-row"
                              key={option.id}
                            >
                              <span>{optionIndex + 1}</span>
                              <input
                                type="text"
                                value={option.label}
                                maxLength={80}
                                disabled={!canManage}
                                placeholder={`Opción ${optionIndex + 1}`}
                                aria-label={`Opción ${optionIndex + 1} de ${field.label}`}
                                onChange={(event) => updateOption(
                                  field.id,
                                  option.id,
                                  event.target.value,
                                )}
                              />
                              {canManage && (
                                <button
                                  type="button"
                                  disabled={field.options.length === 1}
                                  aria-label={`Eliminar opción ${optionIndex + 1}`}
                                  onClick={() => updateField(field.id, {
                                    options: field.options.filter(
                                      ({ id }) => id !== option.id,
                                    ),
                                  })}
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {canManage && (
                          <button
                            className="workflow-field-add-option"
                            type="button"
                            disabled={field.options.length >= 50}
                            onClick={() => updateField(field.id, {
                              options: [...field.options, newOption()],
                            })}
                          >
                            <Plus size={14} />
                            Agregar opción
                          </button>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            {canManage && (
              <button
                className="button secondary workflow-add-field"
                type="button"
                disabled={fields.length >= 50}
                onClick={() => {
                  setFields((current) => [...current, newField()]);
                  setSaved(false);
                }}
              >
                <Plus size={16} />
                Agregar columna
              </button>
            )}
          </section>

          {!canManage && (
            <FeedbackBanner tone="warning">
              Solo un administrador de la organización puede modificar este flujo.
            </FeedbackBanner>
          )}

          {canManage && (
            <div className="workflow-template-form-actions">
              <button
                className="button primary"
                type="submit"
                disabled={!valid || saving}
              >
                {saving ? (
                  <LoaderCircle className="spin" size={17} />
                ) : (
                  <Save size={17} />
                )}
                {saving ? "Guardando…" : "Guardar configuración"}
              </button>
            </div>
          )}

          {templateId && canManage && (
            <section className="workflow-template-settings-card workflow-template-archive-card">
              <div>
                <span className="eyebrow">Administración</span>
                <h2>Archivar flujo</h2>
                <p>
                  El flujo dejará de verse en la lista. Sus registros se conservarán.
                </p>
              </div>
              <button
                className="button danger"
                type="button"
                disabled={archiving}
                onClick={() => setArchiveConfirmationOpen(true)}
              >
                {archiving ? (
                  <LoaderCircle className="spin" size={17} />
                ) : (
                  <Archive size={17} />
                )}
                {archiving ? "Archivando…" : "Archivar flujo"}
              </button>
            </section>
          )}
        </form>
      )}

      {archiveConfirmationOpen && (
        <ConfirmDialog
          title="Archivar flujo"
          description={`“${name.trim() || "Este flujo"}” dejará de verse en la lista de flujos. Sus registros se conservarán.`}
          confirmLabel={archiving ? "Archivando…" : "Archivar flujo"}
          busy={archiving}
          onConfirm={archiveWorkflow}
          onClose={() => {
            if (!archiving) setArchiveConfirmationOpen(false);
          }}
        />
      )}
    </div>
  );
}
